// The Claude menu in the top bar: what Claude is doing (Under way), what it learned, how it works,
// saved answers, asking about your mail, the latest brief and drafts. One quiet button instead of
// a row of links. A plain accessible menu: a real button (aria-haspopup, aria-expanded), a list of
// menuitem links, arrow keys / Home / End move, Escape and a click outside close. Phones list the
// same links under More.
import { useEffect, useId, useRef, useState, type KeyboardEvent } from "react";
import { Link } from "@tanstack/react-router";
import { ChevronDown, Sparkles } from "lucide-react";
import { cn } from "@web/lib/cn";
import { activeClaudeLink, CLAUDE_MENU_LINKS, isNavActive } from "./layout";

export function ClaudeMenu({ pathname, className }: { pathname: string; className?: string }) {
  // Open on the screen it was opened on: going to another screen closes it.
  const [openOn, setOpenOn] = useState<string | null>(null);
  const open = openOn === pathname;
  const setOpen = (next: boolean) => setOpenOn(next ? pathname : null);
  const rootRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const itemRefs = useRef<(HTMLAnchorElement | null)[]>([]);
  const menuId = useId();
  const here = activeClaudeLink(pathname);

  const focusItem = (index: number) => {
    const count = CLAUDE_MENU_LINKS.length;
    itemRefs.current[((index % count) + count) % count]?.focus();
  };

  // Opening focuses the item for this screen, else the first.
  useEffect(() => {
    if (!open) return;
    const start = here ? CLAUDE_MENU_LINKS.indexOf(here) : 0;
    focusItem(Math.max(0, start));
    const onPointer = (e: PointerEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) setOpenOn(null);
    };
    document.addEventListener("pointerdown", onPointer);
    return () => document.removeEventListener("pointerdown", onPointer);
    // Only on opening: moving between screens closes the menu anyway.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const close = (refocus: boolean) => {
    setOpen(false);
    if (refocus) buttonRef.current?.focus();
  };

  const onMenuKey = (e: KeyboardEvent<HTMLUListElement>) => {
    const index = itemRefs.current.findIndex((el) => el === document.activeElement);
    const keys: Record<string, () => void> = {
      ArrowDown: () => focusItem(index + 1),
      ArrowUp: () => focusItem(index - 1),
      Home: () => focusItem(0),
      End: () => focusItem(CLAUDE_MENU_LINKS.length - 1),
      Escape: () => close(true),
    };
    const run = keys[e.key];
    if (run) {
      e.preventDefault();
      // Keep Escape and arrows away from the app's own shortcuts while the menu has focus.
      e.stopPropagation();
      run();
    } else if (e.key === "Tab") {
      setOpen(false);
    }
  };

  return (
    <div ref={rootRef} className={cn("relative", className)}>
      <button
        ref={buttonRef}
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        onClick={() => setOpen(!open)}
        onKeyDown={(e) => {
          if (e.key === "ArrowDown" || e.key === "ArrowUp") {
            e.preventDefault();
            e.stopPropagation();
            setOpen(true);
          }
        }}
        data-testid="claude-menu"
        className={cn(
          "inline-flex h-10 items-center gap-1.5 rounded-lg border px-3 text-sm",
          here || open
            ? "border-line-strong bg-paper text-ink"
            : "border-line bg-paper text-muted hover:text-ink",
        )}
      >
        <Sparkles aria-hidden className="size-4 text-accent-text" />
        Claude
        <ChevronDown
          aria-hidden
          className={cn("size-3.5 transition-transform", open && "rotate-180")}
        />
      </button>
      {open ? (
        <ul
          id={menuId}
          role="menu"
          aria-label="Claude"
          onKeyDown={onMenuKey}
          className="absolute top-full right-0 z-40 mt-1 flex w-60 flex-col rounded-xl border border-line bg-paper p-1 shadow-soft"
        >
          {CLAUDE_MENU_LINKS.map((link, i) => {
            const active = isNavActive(pathname, link);
            const Icon = link.icon;
            return (
              <li key={link.to} role="none">
                <Link
                  ref={(el) => {
                    itemRefs.current[i] = el;
                  }}
                  to={link.to}
                  role="menuitem"
                  tabIndex={-1}
                  aria-current={active ? "page" : undefined}
                  onClick={() => setOpen(false)}
                  className={cn(
                    "flex h-9 items-center gap-2.5 rounded-lg px-2.5 text-sm outline-none",
                    "focus-visible:bg-sunk focus:bg-sunk",
                    active ? "font-medium text-ink" : "text-ink-2 hover:bg-sunk",
                  )}
                >
                  <Icon className="size-4 shrink-0 text-muted" />
                  {link.label}
                </Link>
              </li>
            );
          })}
        </ul>
      ) : null}
    </div>
  );
}
